import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  HasReinforcementPadsOptions,
  InsulatedOptions,
} from 'types/assetModel/assetModelHelpers'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { getDropdownOptionsForEnum } from 'utils/enum'
import {
  optionalNumberSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export interface GeckoLegSupportJSON {
  type: GeckoFeatureModelType.LegSupport
  name: string
  feature_geometry: {
    x: number
    y: number
  }
  surface_specs: {
    leg_diameter: number
    leg_length: number
    nominal_thickness: number | undefined
    insulated: string
    has_reinforcement_pads: string
  }
}

export const NewGeckoLegSupportJSON: GeckoLegSupportJSON = {
  type: GeckoFeatureModelType.LegSupport,
  name: 'Leg Support',
  feature_geometry: {
    x: 0,
    y: 0,
  },
  surface_specs: {
    leg_diameter: 0,
    leg_length: 0,
    nominal_thickness: undefined,
    insulated: InsulatedOptions.No,
    has_reinforcement_pads: HasReinforcementPadsOptions.No,
  },
}

export class GeckoLegSupport extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.LegSupport
  readonly x: GeckoMeasurement
  readonly y: GeckoMeasurement
  readonly legDiameter: GeckoMeasurement
  readonly legLength: GeckoMeasurement
  readonly nominalThickness: GeckoMeasurement | undefined
  readonly insulated: InsulatedOptions
  readonly hasReinforcementPads: HasReinforcementPadsOptions

  constructor(
    id: string,
    name: string,
    x: GeckoMeasurement,
    y: GeckoMeasurement,
    legDiameter: GeckoMeasurement,
    legLength: GeckoMeasurement,
    nominalThickness: GeckoMeasurement | undefined,
    insulated: InsulatedOptions,
    hasReinforcementPads: HasReinforcementPadsOptions
  ) {
    super(id)
    this.name = name
    this.x = x
    this.y = y
    this.legDiameter = legDiameter
    this.legLength = legLength
    this.nominalThickness = nominalThickness
    this.insulated = insulated
    this.hasReinforcementPads = hasReinforcementPads
  }

  public static CreateFromJSON(
    componentID: string,
    legSupportID: string,
    legSupportJSON: GeckoLegSupportJSON
  ): GeckoLegSupport {
    const dimensionMeasurementUnits = GeckoMeasurementUnit.Meters
    const thicknessMeasurementUnits = GeckoMeasurementUnit.Meters

    const featureGeometry = legSupportJSON.feature_geometry as Record<
      string,
      number
    >
    const surfaceSpecs = legSupportJSON.surface_specs as Record<
      string,
      number | string | boolean
    >

    const x = GeckoMeasurement.CreateFromValue(
      featureGeometry.x,
      dimensionMeasurementUnits
    )

    const y = GeckoMeasurement.CreateFromValue(
      featureGeometry.y,
      dimensionMeasurementUnits
    )

    const legDiameter = GeckoMeasurement.CreateFromValue(
      surfaceSpecs.leg_diameter as number,
      dimensionMeasurementUnits
    )

    const legLength = GeckoMeasurement.CreateFromValue(
      surfaceSpecs.leg_length as number,
      dimensionMeasurementUnits
    )

    const nominalThickness =
      surfaceSpecs.nominal_thickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            surfaceSpecs.nominal_thickness as number,
            thicknessMeasurementUnits
          )
        : undefined

    const insulated =
      (surfaceSpecs.insulated as InsulatedOptions) ?? InsulatedOptions.No
    const hasReinforcementPads =
      (surfaceSpecs.has_reinforcement_pads as HasReinforcementPadsOptions) ??
      HasReinforcementPadsOptions.Unknown

    return new GeckoLegSupport(
      legSupportID,
      legSupportJSON.name,
      x,
      y,
      legDiameter,
      legLength,
      nominalThickness,
      insulated,
      hasReinforcementPads
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoLegSupportJSON>
  ): Map<string, GeckoLegSupport> {
    return Object.keys(recordsJSON).reduce((resultObj, legSupportID) => {
      resultObj.set(
        legSupportID,
        GeckoLegSupport.CreateFromJSON(
          componentID,
          legSupportID,
          recordsJSON[legSupportID]
        )
      )
      return resultObj
    }, new Map<string, GeckoLegSupport>())
  }

  asJSON() {
    return generateLegSupport(
      this.name,
      this.x,
      this.y,
      this.legDiameter,
      this.legLength,
      this.nominalThickness,
      this.insulated,
      this.hasReinforcementPads
    )
  }
}

export function generateLegSupport(
  name: string,
  x: GeckoMeasurement,
  y: GeckoMeasurement,
  legDiameter: GeckoMeasurement,
  legLength: GeckoMeasurement,
  nominalThickness?: GeckoMeasurement,
  insulated: InsulatedOptions = InsulatedOptions.No,
  hasReinforcementPads: HasReinforcementPadsOptions = HasReinforcementPadsOptions.No
): GeckoLegSupportJSON {
  const legSupportJSON: GeckoLegSupportJSON = {
    type: GeckoFeatureModelType.LegSupport,
    name: name,
    feature_geometry: {
      x: x.convertTo(GeckoMeasurementUnit.Meters).num,
      y: y.convertTo(GeckoMeasurementUnit.Meters).num,
    },
    surface_specs: {
      leg_diameter: legDiameter.convertTo(GeckoMeasurementUnit.Meters).num,
      leg_length: legLength.convertTo(GeckoMeasurementUnit.Meters).num,
      nominal_thickness:
        nominalThickness !== undefined
          ? nominalThickness.convertTo(GeckoMeasurementUnit.Meters).num
          : undefined,
      insulated: insulated,
      has_reinforcement_pads: hasReinforcementPads,
    },
  }

  return legSupportJSON
}

export const LegSupportFormFields = (legSupportJSON: GeckoLegSupportJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Leg Support Name',
      value: legSupportJSON.name,
      unit_magnitude: null,
      initialValue: 'Leg Support',
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'x',
      label: 'X Position',
      value: legSupportJSON.feature_geometry.x,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: legSupportJSON.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'leg_diameter',
      label: 'Leg Diameter',
      value: legSupportJSON.surface_specs.leg_diameter,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'leg_length',
      label: 'Leg Length',
      value: legSupportJSON.surface_specs.leg_length,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'nominal_thickness',
      label: 'Nominal Thickness',
      value: legSupportJSON.surface_specs.nominal_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'insulated',
      label: 'Insulated',
      value: legSupportJSON.surface_specs.insulated,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
      options: getDropdownOptionsForEnum(InsulatedOptions),
    },
    {
      dataKey: 'has_reinforcement_pads',
      label: 'Has Reinforcement Pads',
      value: legSupportJSON.surface_specs.has_reinforcement_pads,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
      options: getDropdownOptionsForEnum(HasReinforcementPadsOptions),
    },
  ]
}

export const LegSupportJSONFromFormFields = (formFields: FormField[]) => {
  const x = getFormFieldByDataKey(formFields, 'x')
  const y = getFormFieldByDataKey(formFields, 'y')
  const legDiameter = getFormFieldByDataKey(formFields, 'leg_diameter')
  const legLength = getFormFieldByDataKey(formFields, 'leg_length')
  const nominalThickness = getFormFieldByDataKey(
    formFields,
    'nominal_thickness'
  )
  const insulated = getFormFieldByDataKey(formFields, 'insulated')
  const hasReinforcementPads = getFormFieldByDataKey(
    formFields,
    'has_reinforcement_pads'
  )

  return generateLegSupport(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      x?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      y?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      legDiameter?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      legLength?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    nominalThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          nominalThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    (insulated?.value as InsulatedOptions) ?? InsulatedOptions.No,
    (hasReinforcementPads?.value as HasReinforcementPadsOptions) ??
      HasReinforcementPadsOptions.Unknown
  )
}

export const LegSupportFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.LegSupport,
  title: 'Leg Supports',
  singularName: 'Leg Support',
  fields: [
    'name',
    'x',
    'y',
    'leg_diameter',
    'leg_length',
    'nominal_thickness',
    'insulated',
    'has_reinforcement_pads',
  ],
  sortBy: [{ fieldName: 'name', order: 'asc' }],
}
